import useAppsflyerOneLink, { forQrCode, getFallbackOnelinkUrl, useQrCodeCanvas, withAdSet, withCurrentPath } from "components/common/useAppsflyerOneLink";
import Icon from "components/Icons/Icon";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import { useEffect, useRef, useState } from "react";
import { OsTypes, isMobileOnly, osName } from "react-device-detect";
import useReactRouter from "use-react-router";
import apple from "../img/apple-appstore.svg";
import google from "../img/playstore.svg";
import qrCode from "../img/qr-download-app.png";
import { getChromeTranslations } from "../marketing/i18n/pages/chrome";
import "./DownloadAppQrCard.scss";

// Same "S" glyph path used across the product pages (e.g. PackingListPage's cta-strip mark).
const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

const STAR_PATH = "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z";

// Swap this for a QR image encoded with real download-attribution tracking later.
const DISMISS_KEY = "stippl_hide_download_qr_card";
// Desktop: small fixed bottom-left card, revealed after a little scrolling.
const SHOW_AFTER_SCROLL_PX = 500;
// Tablet + mobile: keep the fixed bottom sheet (its original design) but hold it back until the
// footer's legal row is on screen, and render a normal-flow spacer (see .scss) so those links can
// scroll clear of the card instead of staying permanently covered.
const COMPACT_MEDIA = "(max-width: 1024px)";
const SPACER_GAP_PX = 24;

// AppsFlyer's aggregated reporting API groups by Adset but not by sub params beyond af_sub1
// (already spent on the page path) — see withAdSet's own doc comment for the full reasoning.
// Tags this floating card apart from the header "Get the app" modal (AF_ADSET in
// DownloadAppModal.tsx) and the in-flow inline block (InlineInstallCard.tsx), which otherwise
// ride the same OneLink and the same c=web_blog/web_discover campaign.
const AF_ADSET = "floating_qr_card";

interface DownloadAppQrCardProps {
	// "scroll" (default): today's behaviour — reveal after scrolling the window, or (on
	// tablet/mobile) once the footer's legal row scrolls into view. Byte-identical to before this
	// prop existed; every marketing usage relies on this default and must stay unaffected.
	// "immediate": for surfaces that scroll an inner container rather than `window` (public
	// profile, trip share), where window.scrollY never moves and the card would otherwise never
	// appear — show it as soon as it mounts instead.
	reveal?: "scroll" | "immediate";
	// Raises the card clear of B2CBottomNav, which is fixed at the viewport bottom (56px + safe
	// area) below 768px. Set it on any surface rendered inside B2CLayout — without it the card
	// sits on top of the nav's top edge. Measured on an iPhone 13 viewport: 32px of overlap.
	aboveBottomNav?: boolean;
	// Docks the card to the right edge on desktop. The default bottom-left slot is already taken on
	// the in-app surfaces: B2CSidebar is fixed over 0-160px (z-index 100) and ShareContainer's
	// .itinerary panel spans the left ~55% at z-index 901, so the card renders *underneath* both —
	// partially on the profile, entirely on the trip share. Measured on the PR #1232 preview.
	desktopRight?: boolean;
	// The id of an element the card must not cover: while it is on screen the card steps aside, mounted (so its QR stays drawn) but
	// hidden. The country hub's route map passes its section, whose panel keeps its save button in the card's bottom-left slot.
	yieldTo?: string;
}

const DownloadAppQrCard = ({ reveal = "scroll", aboveBottomNav = false, desktopRight = false, yieldTo }: DownloadAppQrCardProps = {}) => {
	const { match } = useReactRouter();
	const [visible, setVisible] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);
	const [spacerHeight, setSpacerHeight] = useState(0);
	const [yielding, setYielding] = useState(false);

	useEffect(() => {
		const target = yieldTo ? document.getElementById(yieldTo) : null;
		if (!target || typeof IntersectionObserver === "undefined") return undefined;
		const io = new IntersectionObserver(([entry]) => setYielding(entry.isIntersecting));
		io.observe(target);
		return () => io.disconnect();
	}, [yieldTo]);

	const lang = useMarketingLang();
	const translations = getChromeTranslations(lang);
	const oneLinkUrl = useAppsflyerOneLink();
	const qrCanvasRef = useRef<HTMLCanvasElement>(null);
	const currentPath = typeof window === "undefined" ? "" : window.location.pathname;
	// oneLinkUrl is cached from whichever page the Smart Script first ran on — rewrite af_sub1 so it
	// still reflects the page the card is actually showing on, without re-triggering AppsFlyer's SDK
	// (which would risk losing the first-touch mediaSource/campaign values). Falls back to
	// getFallbackOnelinkUrl() (still OS-smart-redirects, just without attribution) if OneLink never
	// resolves. withAdSet applies to both branches — the fallback link is exactly as separable from
	// the other install surfaces as the live one.
	const trackedUrl = withAdSet(oneLinkUrl ? withCurrentPath(oneLinkUrl, currentPath) : getFallbackOnelinkUrl(), AF_ADSET);
	// Gated on visible/dismissed/isMobileOnly too — the canvas below only exists once the card is
	// actually shown (post-scroll, desktop only), so drawing must wait until then. forQrCode strips
	// the Smart Script's own bookkeeping params and marks the scan with af_channel=qr — taps on the
	// hrefs below keep trackedUrl as-is, only the rendered QR payload goes through it.
	const qrRendered = useQrCodeCanvas(qrCanvasRef, !isMobileOnly && visible && !dismissed, forQrCode(trackedUrl), {
		colorDark: "#1b3d49",
	});

	useEffect(() => {
		if (sessionStorage.getItem(DISMISS_KEY)) return undefined;

		// Surfaces that scroll an inner container rather than `window` (public profile, trip
		// share) opt into showing the card immediately — window.scrollY there never moves, and
		// there is no ".stippl-footer" to intersect either, so the scroll/footer watchers below
		// would never fire.
		if (reveal === "immediate") {
			setVisible(true);
			return undefined;
		}

		const compactMq = window.matchMedia(COMPACT_MEDIA);
		let teardown = () => {};

		// Desktop: reveal the fixed card once the visitor has scrolled a bit.
		const watchScroll = () => {
			const handleScroll = () => {
				if (window.scrollY > SHOW_AFTER_SCROLL_PX) {
					setVisible(true);
					window.removeEventListener("scroll", handleScroll);
				}
			};
			window.addEventListener("scroll", handleScroll, { passive: true });
			return () => window.removeEventListener("scroll", handleScroll);
		};

		// Tablet/mobile: hold the card back until the footer's legal row (copyright + Terms/Privacy/
		// Disclaimer links) has scrolled into view.
		const watchFooter = () => {
			const legalRow = document.querySelector(".stippl-footer .footer-row");
			if (!legalRow) return watchScroll();

			const io = new IntersectionObserver((entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					io.disconnect();
					setVisible(true);
				}
			});
			io.observe(legalRow);
			return () => io.disconnect();
		};

		const apply = () => {
			teardown();
			teardown = compactMq.matches ? watchFooter() : watchScroll();
		};
		apply();
		compactMq.addEventListener("change", apply);
		return () => {
			compactMq.removeEventListener("change", apply);
			teardown();
		};
	}, [reveal]);

	// Tablet/mobile only: size the normal-flow spacer to the fixed card so the footer's legal links
	// can be scrolled clear of it. On desktop the spacer is display:none, so this is inert there.
	useEffect(() => {
		if (!visible || dismissed) return undefined;
		const measure = () => setSpacerHeight(cardRef.current?.offsetHeight ?? 0);
		measure();
		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
	}, [visible, dismissed]);

	const handleDismiss = () => {
		sessionStorage.setItem(DISMISS_KEY, "1");
		setDismissed(true);
	};

	const navClass = `${aboveBottomNav ? " download-app-qr-card--above-bottom-nav" : ""}${desktopRight ? " download-app-qr-card--desktop-right" : ""}${yielding ? " download-app-qr-card--yield" : ""}`;

	const handleTracking = (type: "apple" | "android") => {
		trackEvent(type === "android" ? "click_googleplay_cta" : "click_appstore_cta", { placement: "floating_card" }, "marketing_site");
		trackFathom(type === "android" ? "BNQKH0MH" : "FD21GVOP");
	};

	if (dismissed || !visible) return null;

	const closeButton = (
		<button type="button" className="download-app-qr-card__close" aria-label={t(translations, "qrCard.close", "Close")} onClick={handleDismiss}>
			<Icon name="close" size={10} fill="currentColor" />
		</button>
	);

	const stars = (size: number) => (
		<div className="download-app-qr-card__stars">
			{[0, 1, 2, 3, 4].map((i) => (
				<svg key={i} width={size} height={size} viewBox="0 0 24 24" fill="#ffc14a">
					<path d={STAR_PATH} />
				</svg>
			))}
		</div>
	);

	// Normal-flow filler (tablet/mobile only, see .scss) that lets the footer's legal links scroll
	// out from under the fixed card.
	const spacer = (
		<div className="download-app-qr-card__spacer" aria-hidden="true" style={{ height: spacerHeight ? spacerHeight + SPACER_GAP_PX : undefined }} />
	);

	if (isMobileOnly) {
		const isAndroid = osName === OsTypes.Android;

		return (
			<>
				{spacer}
				<div ref={cardRef} className={`download-app-qr-card download-app-qr-card--mobile${navClass}`}>
					{closeButton}
					<p className="download-app-qr-card__mobile-title">{t(translations, "qrCard.mobileTitle", "Download the Stippl app now")}</p>
					<div className="download-app-qr-card__mobile-rating">
						<div className="download-app-qr-card__rating__logo">
							<svg width="13" height="17" viewBox="0 0 81 108" fill="none">
								<path d={S_PATH} fill="#fff" />
							</svg>
						</div>
						<div className="download-app-qr-card__mobile-rating__meta">
							{stars(18)}
							<span>4.7/5</span>
						</div>
					</div>
					<a
						href={trackedUrl}
						className="download-app-qr-card__mobile-button"
						target="_blank"
						rel="noreferrer"
						onClick={() => handleTracking(isAndroid ? "android" : "apple")}
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
							<path d="M12 3v13M7 11l5 5 5-5"></path>
							<path d="M5 21h14"></path>
						</svg>
						<span>{t(translations, "qrCard.mobileButton", "Download the app")}</span>
					</a>
				</div>
			</>
		);
	}

	return (
		<>
			{spacer}
			<div ref={cardRef} className={`download-app-qr-card${navClass}`}>
				{closeButton}
				<div className="download-app-qr-card__qr">
					<canvas ref={qrCanvasRef} className="download-app-qr-card__qr-canvas" width={124} height={124} style={{ display: qrRendered ? "block" : "none" }} />
					{!qrRendered && <img src={qrCode} alt={t(translations, "qrCard.qrAlt", "Scan to download the Stippl app")} width={124} height={124} />}
				</div>
				<div className="download-app-qr-card__content">
					<svg className="download-app-qr-card__arrow" width="46" height="52" viewBox="0 0 46 52" fill="none">
						<path d="M38 4C45 24 40 42 12 44" stroke="#13c892" stroke-width="2.4" stroke-linecap="round" fill="none"></path>
						<path d="M23 38L11 44L21 50" stroke="#13c892" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"></path>
					</svg>
					<h4>
						{t(translations, "qrCard.desktopHeadingLine1", "Scan the QR code")}
						<br />
						{t(translations, "qrCard.desktopHeadingLine2", "to download!")}
					</h4>
					<div className="download-app-qr-card__rating">
						<div className="download-app-qr-card__rating__logo">
							<svg width="16" height="16" viewBox="0 0 81 108" fill="none">
								<path d={S_PATH} fill="#fff" />
							</svg>
						</div>
						{stars(12)}
						<span>4.7/5</span>
					</div>
					<div className="download-app-qr-card__badges">
						<a
							href={trackedUrl}
							aria-label={t(translations, "qrCard.appStoreLabel", "Download Stippl on the App Store")}
							target="_blank"
							rel="noreferrer"
							onClick={() => handleTracking("apple")}
						>
							<img src={apple} width={104} height={31} alt={t(translations, "qrCard.appStoreAlt", "App Store")} />
						</a>
						<a
							href={trackedUrl}
							aria-label={t(translations, "qrCard.googlePlayLabel", "Get Stippl on Google Play")}
							target="_blank"
							rel="noreferrer"
							onClick={() => handleTracking("android")}
						>
							<img src={google} width={104} height={31} alt={t(translations, "qrCard.googlePlayAlt", "Google Play")} />
						</a>
					</div>
				</div>
			</div>
		</>
	);
};

export default DownloadAppQrCard;
